import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { CONNECTING_DELAY_MS, DEFAULT_TOPOLOGY, MAX_LOG_EVENTS } from "@/features/network-topology/constants";
import {
  addNode as engineAddNode,
  clearAllFaults as engineClearAllFaults,
  removeNode as engineRemoveNode,
  setFaulted as engineSetFaulted,
  setLinkDisconnected as engineSetLinkDisconnected,
  setLinkType as engineSetLinkType,
  pickFaultCandidates,
  setManual,
  setSimActive,
} from "@/features/network-topology/engine";
import type {
  ConnectionType,
  LogEvent,
  LogEventKind,
  NetworkNode,
  NodeKind,
  SimBinding,
} from "@/features/network-topology/types";

/**
 * ネットワークトポロジ Zustand ストア。
 *
 * - persist middleware で localStorage に保存（key: factory-twin-network）
 * - 接続中の擬似遅延は store 内で setTimeout で管理（タイマー id を ref で保持）
 * - sim 連動 (sim → network) は setSimActive / appendEvent を経由
 *
 * イベントログは末尾追加で `MAX_LOG_EVENTS` で先頭から切り詰める。
 */

interface NetworkStoreState {
  nodes: NetworkNode[];
  /** 排他選択: ノードを選択するとエッジ選択は外れる */
  selectedNodeId: string | null;
  /** 排他選択: エッジ選択。子ノード id で識別（id 1:1 対応） */
  selectedEdgeChildId: string | null;
  events: LogEvent[];
}

interface NetworkStoreActions {
  selectNode(id: string | null): void;
  /** エッジ (親→子) を選択。childNodeId で一意に識別 */
  selectEdge(childNodeId: string | null): void;

  /** ノードを切断 (manual=offline)。配下は computed で unreachable に。 */
  disconnectNode(id: string): void;
  /** 接続開始 (manual=connecting → 2秒後 online) */
  startConnect(id: string): void;
  /** 即座に online に戻す（connecting タイマーキャンセル含む） */
  forceOnline(id: string): void;

  /**
   * 任意の親配下に新規ノードを追加。
   * `id` を指定すれば明示 id (P5 sim ↔ network 同期で使用)。衝突時は null。
   */
  addNode(spec: {
    kind: NodeKind;
    label: string;
    parentId: string;
    simBinding?: SimBinding | null;
    id?: string;
    linkType?: ConnectionType;
  }): string | null;
  /** 配下の子孫含めて削除 (root 不可) */
  removeNode(id: string): void;

  /** リンクを切断 (子側で linkDisconnected=true) */
  disconnectLink(childNodeId: string): void;
  /** リンクを再接続 (擬似遅延 + linkDisconnected=false) */
  startConnectLink(childNodeId: string): void;
  /** リンクの接続方式を変更 */
  setLinkType(childNodeId: string, linkType: ConnectionType): void;

  /** sim → network: 活動フラグ反映 */
  setSimActiveFor(binding: SimBinding["kind"], active: boolean): void;

  /** 通信異常フラグの設定（デモ・自動検知共通） */
  setFaulted(id: string, faulted: boolean): void;
  /** デモ: ランダムな1ノードに障害注入。注入したノード id を返す（候補なしなら null） */
  injectRandomFault(): string | null;
  /** すべての fault フラグをクリア */
  clearAllFaults(): void;

  /** イベントログ追加 */
  appendEvent(input: { kind: LogEventKind; nodeId: string | null; message: string }): void;
  clearEvents(): void;

  /** トポロジを既定値にリセット */
  resetToDefault(): void;
}

export type NetworkStore = NetworkStoreState & NetworkStoreActions;

/**
 * connecting タイマー管理 (module-level ref)。
 * 同じノードに対して連続して startConnect が走った場合、前回タイマーを破棄する。
 */
const connectingTimers = new Map<string, ReturnType<typeof setTimeout>>();

/**
 * リンク再接続タイマー管理。
 * connectingTimers (ノード用) とは独立に、`link:<childId>` を key として保持。
 */
const connectingLinkTimers = new Map<string, ReturnType<typeof setTimeout>>();

function cancelConnectingTimer(nodeId: string): void {
  const t = connectingTimers.get(nodeId);
  if (t !== undefined) {
    clearTimeout(t);
    connectingTimers.delete(nodeId);
  }
}

function cancelConnectingLinkTimer(childId: string): void {
  const t = connectingLinkTimers.get(childId);
  if (t !== undefined) {
    clearTimeout(t);
    connectingLinkTimers.delete(childId);
  }
}

/** 時刻 ms epoch + ランダム suffix で id を作る */
function newEventId(): string {
  return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export const useNetworkStore = create<NetworkStore>()(
  devtools(
    persist(
      (set, get) => ({
        nodes: DEFAULT_TOPOLOGY,
        selectedNodeId: null,
        selectedEdgeChildId: null,
        events: [],

        selectNode(id) {
          set({ selectedNodeId: id, selectedEdgeChildId: null }, false, "network/selectNode");
        },

        selectEdge(childNodeId) {
          set({ selectedEdgeChildId: childNodeId, selectedNodeId: null }, false, "network/selectEdge");
        },

        disconnectNode(id) {
          cancelConnectingTimer(id);
          set((s) => ({ nodes: setManual(s.nodes, id, "offline") }), false, "network/disconnectNode");
          const node = get().nodes.find((n) => n.id === id);
          if (node) {
            get().appendEvent({
              kind: "warning",
              nodeId: id,
              message: `${node.label}: link down (manual disconnect)`,
            });
          }
        },

        startConnect(id) {
          cancelConnectingTimer(id);
          set((s) => ({ nodes: setManual(s.nodes, id, "connecting") }), false, "network/startConnect");
          const node = get().nodes.find((n) => n.id === id);
          if (node) {
            get().appendEvent({
              kind: "info",
              nodeId: id,
              message: `${node.label}: connecting...`,
            });
          }
          const timer = setTimeout(() => {
            connectingTimers.delete(id);
            const cur = get().nodes.find((n) => n.id === id);
            if (cur && cur.manual === "connecting") {
              set((s) => ({ nodes: setManual(s.nodes, id, "online") }), false, "network/connectComplete");
              get().appendEvent({
                kind: "info",
                nodeId: id,
                message: `${cur.label}: link up`,
              });
            }
          }, CONNECTING_DELAY_MS);
          connectingTimers.set(id, timer);
        },

        forceOnline(id) {
          cancelConnectingTimer(id);
          set((s) => ({ nodes: setManual(s.nodes, id, "online") }), false, "network/forceOnline");
        },

        disconnectLink(childNodeId) {
          cancelConnectingLinkTimer(childNodeId);
          set(
            (s) => ({ nodes: engineSetLinkDisconnected(s.nodes, childNodeId, true) }),
            false,
            "network/disconnectLink",
          );
          const node = get().nodes.find((n) => n.id === childNodeId);
          if (node) {
            get().appendEvent({
              kind: "warning",
              nodeId: childNodeId,
              message: `Link to ${node.label}: 切断 (manual)`,
            });
          }
        },

        startConnectLink(childNodeId) {
          cancelConnectingLinkTimer(childNodeId);
          // 接続開始は即時には online に戻さず、
          // 擬似遅延の間は linkDisconnected=true のまま (UI 上は破線のまま) にしておく
          // こうしないとリンクが瞬時に復活してしまい "接続中" の演出が見えない。
          const node = get().nodes.find((n) => n.id === childNodeId);
          if (node) {
            get().appendEvent({
              kind: "info",
              nodeId: childNodeId,
              message: `Link to ${node.label}: connecting...`,
            });
          }
          const timer = setTimeout(() => {
            connectingLinkTimers.delete(childNodeId);
            const cur = get().nodes.find((n) => n.id === childNodeId);
            if (cur?.linkDisconnected) {
              set(
                (s) => ({ nodes: engineSetLinkDisconnected(s.nodes, childNodeId, false) }),
                false,
                "network/connectLinkComplete",
              );
              get().appendEvent({
                kind: "info",
                nodeId: childNodeId,
                message: `Link to ${cur.label}: link up`,
              });
            }
          }, CONNECTING_DELAY_MS);
          connectingLinkTimers.set(childNodeId, timer);
        },

        setLinkType(childNodeId, linkType) {
          set((s) => ({ nodes: engineSetLinkType(s.nodes, childNodeId, linkType) }), false, "network/setLinkType");
          const node = get().nodes.find((n) => n.id === childNodeId);
          if (node) {
            get().appendEvent({
              kind: "info",
              nodeId: childNodeId,
              message: `Link to ${node.label}: 接続方式を ${linkType.toUpperCase()} へ変更`,
            });
          }
        },

        addNode(spec) {
          const result = engineAddNode(get().nodes, spec);
          if (!result) return null;
          set({ nodes: result.nodes }, false, "network/addNode");
          get().appendEvent({
            kind: "info",
            nodeId: result.addedId,
            message: `Added ${spec.label} (${spec.kind}) under ${spec.parentId}`,
          });
          return result.addedId;
        },

        removeNode(id) {
          const target = get().nodes.find((n) => n.id === id);
          cancelConnectingTimer(id);
          set(
            (s) => ({
              nodes: engineRemoveNode(s.nodes, id),
              selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
            }),
            false,
            "network/removeNode",
          );
          if (target) {
            get().appendEvent({
              kind: "warning",
              nodeId: null,
              message: `Removed ${target.label} (${target.kind}) and its descendants`,
            });
          }
        },

        setSimActiveFor(binding, active) {
          set((s) => ({ nodes: setSimActive(s.nodes, binding, active) }), false, "network/setSimActive");
        },

        setFaulted(id, faulted) {
          set((s) => ({ nodes: engineSetFaulted(s.nodes, id, faulted) }), false, "network/setFaulted");
          const node = get().nodes.find((n) => n.id === id);
          if (node) {
            get().appendEvent({
              kind: faulted ? "error" : "info",
              nodeId: id,
              message: faulted ? `${node.label}: 通信異常を検知` : `${node.label}: 異常状態を解除`,
            });
          }
        },

        injectRandomFault() {
          const candidates = pickFaultCandidates(get().nodes);
          if (candidates.length === 0) return null;
          const pick = candidates[Math.floor(Math.random() * candidates.length)];
          if (pick === undefined) return null;
          get().setFaulted(pick, true);
          return pick;
        },

        clearAllFaults() {
          set({ nodes: engineClearAllFaults(get().nodes) }, false, "network/clearAllFaults");
          get().appendEvent({
            kind: "info",
            nodeId: null,
            message: "すべての異常状態を解除",
          });
        },

        appendEvent(input) {
          const ev: LogEvent = {
            id: newEventId(),
            at: Date.now(),
            kind: input.kind,
            nodeId: input.nodeId,
            message: input.message,
          };
          set((s) => ({ events: [ev, ...s.events].slice(0, MAX_LOG_EVENTS) }), false, "network/appendEvent");
        },

        clearEvents() {
          set({ events: [] }, false, "network/clearEvents");
        },

        resetToDefault() {
          for (const t of connectingTimers.values()) clearTimeout(t);
          connectingTimers.clear();
          for (const t of connectingLinkTimers.values()) clearTimeout(t);
          connectingLinkTimers.clear();
          set(
            { nodes: DEFAULT_TOPOLOGY, selectedNodeId: null, selectedEdgeChildId: null, events: [] },
            false,
            "network/reset",
          );
        },
      }),
      {
        name: "factory-twin-network",
        // localStorage に保存するのは "ユーザーの意図的な状態" のみ。
        // - simActive / connecting / events は揮発させたい（再読込で消える）
        partialize: (state) => ({
          nodes: state.nodes.map((n) => ({
            ...n,
            // connecting は再読込で online に戻す（タイマーが失われるため）
            manual: n.manual === "connecting" ? "online" : n.manual,
            // simActive / faulted はランタイムフラグなので保存しない（再読込で消える）
            simActive: undefined,
            faulted: undefined,
            // linkDisconnected はユーザー意図なので保存（再読込でも維持）
            // linkType も同じく保存
          })),
        }),
      },
    ),
    { name: "NetworkStore" },
  ),
);
